/**
 * Global Axios Interceptor Setup
 * This configures all axios instances (both default axios and apiClient) to call /auth/me before requests
 */

import axios from 'axios'
import { toast } from 'react-toastify'
import { getCurrentApiUrl } from '../utils/backendConfig'
import { getAuthHeaders, clearStoredAuth } from '../services/authTokenService'

// Simple debouncing to prevent too many /auth/me calls
let lastAuthMeCall = 0
const MIN_AUTH_ME_INTERVAL = 5000 // 5 seconds minimum between calls

// Track ongoing auth check to prevent multiple simultaneous calls
let authCheckInProgress = false

// Helper function to force logout
const forceLogout = () => {
  clearStoredAuth()
  
  // Redirect to login page
  if (window.location.pathname !== '/login') {
    window.location.href = '/login'
  }
}

// Helper function to call /auth/me and check user status
const checkUserStatus = async () => {
  try {
    // Prevent multiple simultaneous auth checks
    if (authCheckInProgress) {
      return true
    }
    
    // Simple debouncing - increased interval to prevent loops
    const now = Date.now()
    if (now - lastAuthMeCall < MIN_AUTH_ME_INTERVAL) {
      return true // Skip check if called too recently
    }
    
    authCheckInProgress = true
    lastAuthMeCall = now
    
    const tokenObj = localStorage.getItem('kt-auth-react-v')
    if (!tokenObj) {
      authCheckInProgress = false
      return false
    }
    
    const parsedTokenObj = JSON.parse(tokenObj)
    const token = parsedTokenObj.laravel_token ?? parsedTokenObj.token
    
    if (!token) {
      authCheckInProgress = false
      return false
    }
    
    // Call /auth/me directly with proper headers for current backend
    const authHeaders = getAuthHeaders()
    const response = await axios.get(`${getCurrentApiUrl()}/auth/me`, {
      headers: {
        ...authHeaders,
        'Accept': 'application/json',
        'bypass-global-interceptor': 'true' // Special header to prevent infinite loops
      },
      timeout: 5000 // Add timeout to prevent hanging requests
    })
    
    const userData = response.data?.data || response.data?.user || response.data
    
    // Update stored auth data with fresh user data
    if (userData) {
      const updatedAuth = {
        ...parsedTokenObj,
        user: userData
      }
      localStorage.setItem('kt-auth-react-v', JSON.stringify(updatedAuth))
      
      // Trigger Redux state update by dispatching a custom event
      window.dispatchEvent(new CustomEvent('auth-user-updated', { detail: userData }))
    }
    
    // Check if user is disabled - handle both number and string status
    if (userData && (userData.status === 0 || userData.status === "0" || userData.status === false)) {
      authCheckInProgress = false
      forceLogout()
      return false
    }
    
    authCheckInProgress = false
    return true
  } catch (error) {
    authCheckInProgress = false
    // If 401 unauthorized, force logout
    if ((error as any).response?.status === 401) {
      forceLogout()
      return false
    }
    // For other errors, continue with the request
    return true
  }
}

// Set up global axios interceptor
const setupGlobalAxiosInterceptor = () => {
  // Add request interceptor to default axios instance using defaults
  axios.defaults.timeout = 10000
  
  // Add request interceptor to default axios instance
  axios.interceptors.request.use(
    async (config) => {
      
      // Skip if this is the bypass request (to prevent infinite loops)
      if (config.headers?.['bypass-global-interceptor']) {
        return config
      }
      
      // Set dynamic base URL for each request
      config.baseURL = getCurrentApiUrl()
      
      // Skip auth/me call for login, logout, auth/me, and public endpoints to avoid infinite loops
      const skipAuthMeEndpoints = [
        '/auth/login', 
        '/auth/logout', 
        '/auth/me', 
        '/auth/register',
        '/auth/forgot-password',
        '/auth/reset-password',
        '/auth/verify-reset-token',
        '/users/billing-personnel/invitation',
        '/users/billing-personnel/register'
      ]
      const isSkipAuthMeEndpoint = skipAuthMeEndpoints.some(endpoint => config.url?.includes(endpoint))
      
      // Always call /auth/me before any API request (except for the excluded endpoints)
      if (!isSkipAuthMeEndpoint) {
        const isUserValid = await checkUserStatus()
        if (!isUserValid) {
          return Promise.reject(new Error('User account has been disabled or unauthorized'))
        }
      }
      
      // Set authentication headers based on current backend
      const authHeaders = getAuthHeaders()
      config.headers = {
        ...config.headers,
        ...authHeaders
      }
          
      return config
    },
    (error) => Promise.reject(error)
  )

  // Add response interceptor to handle auth errors
  axios.interceptors.response.use(
    (response) => {
      return response
    },
    (error) => {
      const status = (error as any).response?.status

      // Handle unauthorized errors by forcing logout
      if (status === 401) {
        forceLogout()
      }

      // Handle rate limiting (429) gracefully with deduplicated toast
      if (status === 429) {
        toast.error('Too many requests. Please wait a moment and try again.', {
          toastId: 'rate-limit-error', // Prevents duplicate toasts
          autoClose: 5000,
        })
      }

      return Promise.reject(error)
    }
  )
}

// Initialize the global interceptor
setupGlobalAxiosInterceptor()

export default setupGlobalAxiosInterceptor
